import { safeAuthReturn } from '@/services/authReturn';
import GoogleContinueButton from './components/GoogleContinueButton';
import ExternalContinueButtons from './components/ExternalContinueButtons';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import AgreementModal from './components/AgreementModal';
import { AGREEMENTS } from './agreementContent';
import forexMindLogo from '@/assets/forexmind-logo.png';

const OTP_LENGTH = 6;

function normalizeOtpCode(value: string) {
  return value.replace(/\D/g, '').slice(0, OTP_LENGTH);
}

function OtpCodeInput({
  value,
  onChange,
  disabled,
  autoFocusKey,
  ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  autoFocusKey: string | number | boolean;
  ariaLabel: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (disabled) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, [autoFocusKey, disabled]);

  const activeIndex = Math.min(value.length, OTP_LENGTH - 1);

  return (
    <div
      className="relative flex items-center gap-2.5 mb-7"
      onClick={() => inputRef.current?.focus()}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={OTP_LENGTH}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={event => onChange(normalizeOtpCode(event.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 z-10 h-full w-full border-0 bg-transparent p-0 text-transparent outline-none"
        style={{
          caretColor: 'transparent',
          WebkitAppearance: 'none',
          appearance: 'none',
        }}
      />
      {Array.from({ length: OTP_LENGTH }).map((_, idx) => {
        const digit = value[idx] || '';
        const isFilled = Boolean(digit);
        const isActive = focused && !disabled && idx === activeIndex;
        return (
          <div
            key={idx}
            aria-hidden="true"
            className="flex-1 min-w-0 text-center font-semibold rounded-xl transition-all flex items-center justify-center"
            style={{
              height: '56px',
              fontSize: '22px',
              letterSpacing: '0',
              background: isFilled ? '#F5F3FF' : '#FAFAFB',
              border: `1.5px solid ${
                isFilled ? '#4F46E5' : isActive ? '#A5B4FC' : '#E5E7EB'
              }`,
              color: isFilled ? '#4F46E5' : '#111827',
              boxShadow: isFilled
                ? '0 0 0 3px rgba(79,70,229,0.08)'
                : isActive
                ? '0 0 0 3px rgba(165,180,252,0.12)'
                : 'none',
            }}
          >
            {digit}
          </div>
        );
      })}
    </div>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, requestLoginOtp, verifyLoginOtp, verifyLogin2Fa } = useAuth();

  const params = new URLSearchParams(location.search);
  const candidateRedirect = params.get('redirect') || (location.state as { from?: unknown } | null)?.from;
  const redirectTo = safeAuthReturn(candidateRedirect);

  const [email, setEmail] = useState('');
  const [invitationCode, setInvitationCode] = useState(() =>
    (params.get('invitation_code') || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 24).toUpperCase());
  const [emailTouched, setEmailTouched] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(true);
  const [termsVersion, setTermsVersion] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState('');
  const [debugOtpCode, setDebugOtpCode] = useState<string | null>(null);

  /* 2FA states */
  const [needs2FA, setNeeds2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAutoLoginOtpRef = useRef('');

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const emailValid = isValidEmail(email);
  const emailError = emailTouched && email.trim() && !emailValid;

  const otpFull = otpCode.length === OTP_LENGTH;
  const twoFAFull = twoFACode.length === OTP_LENGTH;

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [countdown]);

  const doSendOtp = async () => {
    setSendLoading(true);
    setError('');
    lastAutoLoginOtpRef.current = '';
    try {
      const result = await requestLoginOtp(email.trim(), invitationCode);
      setChallengeId(result.challenge_id);
      setDebugOtpCode(result.delivery_mode === 'fixed_code' ? result.debug_otp_code || null : null);
      setRequiresTermsAcceptance(result.requires_terms_acceptance !== false);
      setTermsVersion(result.terms_version || null);
      setAgreementAccepted(prev => result.requires_terms_acceptance === false || prev);
      setOtpSent(true);
      setOtpCode('');
      setCountdown(result.rate_seconds || 60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '验证码发送失败，请稍后重试');
    } finally {
      setSendLoading(false);
    }
  };

  const handleContinue = async () => {
    setError('');
    setEmailTouched(true);
    if (!emailValid) { setError('请输入正确的邮箱地址'); return; }

    await doSendOtp();
  };

  const handleResend = async () => {
    if (countdown > 0 || !emailValid) return;
    setError('');
    setOtpCode('');
    lastAutoLoginOtpRef.current = '';
    setSendLoading(true);
    try {
      const result = await requestLoginOtp(email.trim(), invitationCode);
      setChallengeId(result.challenge_id);
      setDebugOtpCode(result.delivery_mode === 'fixed_code' ? result.debug_otp_code || null : null);
      setRequiresTermsAcceptance(result.requires_terms_acceptance !== false);
      setTermsVersion(result.terms_version || null);
      setAgreementAccepted(prev => result.requires_terms_acceptance === false || prev);
      setCountdown(result.rate_seconds || 60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '验证码发送失败，请稍后重试');
    } finally {
      setSendLoading(false);
    }
  };

  const submitOtpCode = useCallback(async (otpCode: string, agreedOverride = agreementAccepted) => {
    if (!challengeId || verifyLoading || otpCode.length !== 6) return;
    if (requiresTermsAcceptance && !agreedOverride) {
      setShowAgreement(true);
      return;
    }
    setError('');
    setVerifyLoading(true);
    try {
      const result = await verifyLoginOtp({
        challengeId,
        otp: otpCode,
        agreedTerms: requiresTermsAcceptance ? agreedOverride : false,
      });
      if (result.auth_status === '2fa_required') {
        setTempToken(result.temp_token || '');
        setNeeds2FA(true);
        setTwoFACode('');
        setTwoFAError('');
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '验证码错误或已过期');
    } finally {
      setVerifyLoading(false);
    }
  }, [
    agreementAccepted,
    challengeId,
    navigate,
    redirectTo,
    requiresTermsAcceptance,
    verifyLoading,
    verifyLoginOtp,
  ]);

  const doLogin = useCallback((agreedOverride = agreementAccepted) => {
    void submitOtpCode(otpCode, agreedOverride);
  }, [agreementAccepted, otpCode, submitOtpCode]);

  const handleBackFromOtp = () => {
    setOtpSent(false);
    setOtpCode('');
    setChallengeId('');
    setDebugOtpCode(null);
    setCountdown(0);
    setError('');
    lastAutoLoginOtpRef.current = '';
    setTimeout(() => document.getElementById('email')?.focus(), 0);
  };

  const handleBackFrom2FA = () => {
    setNeeds2FA(false);
    setTwoFACode('');
    setTwoFAError('');
    setTempToken('');
  };

  useEffect(() => {
    if (!otpFull) {
      lastAutoLoginOtpRef.current = '';
      return;
    }
    if (!challengeId || verifyLoading || showAgreement) return;
    if (lastAutoLoginOtpRef.current === otpCode) return;
    lastAutoLoginOtpRef.current = otpCode;
    void submitOtpCode(otpCode);
  }, [challengeId, otpCode, otpFull, showAgreement, submitOtpCode, verifyLoading]);

  const handle2FAVerify = async () => {
    if (!twoFAFull) return;
    setTwoFALoading(true);
    setTwoFAError('');
    try {
      await verifyLogin2Fa({ tempToken, totpCode: twoFACode });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setTwoFAError(err instanceof ApiError ? err.message : '验证码错误，请重新输入');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleAgreementConfirm = () => {
    setAgreementAccepted(true);
    setShowAgreement(false);
    if (otpFull && challengeId) {
      setTimeout(() => void doLogin(true), 0);
    }
  };

  const brandPrimary = '#4F46E5';
  const brandSecondary = '#7C3AED';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: '#FFFFFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* ─────────────── Email Input State ─────────────── */}
      {!otpSent && !needs2FA && (
        <div className="flex-1 flex flex-col px-6" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 80px)' }}>
          {/* Logo + Brand */}
          <div className="flex flex-col items-center mb-9">
            <div className="mb-4">
              <img
                src={forexMindLogo}
                alt="ForexMind"
                className="w-14 h-14 object-contain"
                style={{ filter: 'drop-shadow(0 8px 20px rgba(15, 23, 42, 0.14))' }}
              />
            </div>
            <h1
              className="font-bold tracking-tight"
              style={{
                fontSize: '28px',
                color: '#111827',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              ForexMind
            </h1>
            <p
              style={{
                fontSize: '15px',
                color: '#9CA3AF',
                marginTop: '6px',
                fontWeight: 400,
                letterSpacing: '0.01em',
              }}
            >
              AI 外汇决策助手
            </p>
          </div>

          {/* Email Input */}
          <div className="w-full max-w-sm mx-auto">
            {invitationCode ? <p className="mb-4 text-sm text-gray-600">本次包含邀请码，请使用邮箱登录以保留邀请归属。</p> : <><GoogleContinueButton onSuccess={() => navigate(redirectTo, { replace: true })} /><ExternalContinueButtons onSuccess={() => navigate(redirectTo, { replace: true })} /><p className="my-4 text-center text-base text-gray-500">或使用邮箱</p></>}
            <label
              htmlFor="email"
              style={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#6B7280',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              邮箱地址
            </label>
            <div
              className="flex items-center w-full overflow-hidden"
              style={{
                height: '56px',
                borderRadius: '16px',
                border: `1.5px solid ${emailError ? '#EF4444' : emailValid ? '#4F46E5' : '#E5E7EB'}`,
                background: '#FAFAFB',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: emailError
                  ? '0 0 0 3px rgba(239,68,68,0.08)'
                  : emailValid
                  ? '0 0 0 3px rgba(79,70,229,0.08)'
                  : 'none',
              }}
            >
              <div className="w-11 flex-shrink-0 flex items-center justify-center">
                <i
                  className="ri-mail-line"
                  style={{
                    fontSize: '18px',
                    color: emailError ? '#EF4444' : emailValid ? '#4F46E5' : '#9CA3AF',
                  }}
                />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onBlur={() => setEmailTouched(true)}
                onKeyDown={e => e.key === 'Enter' && handleContinue()}
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                className="flex-1 min-w-0 bg-transparent outline-none border-none pl-2 pr-4"
                style={{
                  color: '#111827',
                  fontSize: '16px',
                  height: '100%',
                  caretColor: '#4F46E5',
                }}
              />
            </div>
            {emailError && (
              <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '8px' }}>
                请输入正确的邮箱地址
              </p>
            )}

            <label htmlFor="invitation-code" className="block text-[13px] font-medium text-gray-500">
              邀请人的邀请码（选填）
            </label>
            <input
              id="invitation-code"
              value={invitationCode}
              onChange={event => setInvitationCode(event.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24).toUpperCase())}
              maxLength={24}
              autoCapitalize="characters"
              autoComplete="off"
              placeholder="输入邀请人的邀请码"
              className="mt-2 h-14 w-full rounded-2xl border border-gray-200 bg-[#FAFAFB] pl-[52px] pr-4 text-base text-[#111827] outline-none focus:border-[#4F46E5]"
            />

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={sendLoading}
              className="w-full font-semibold transition-all mt-6 flex-shrink-0"
              style={{
                height: '56px',
                minHeight: '56px',
                borderRadius: '16px',
                fontSize: '16px',
                letterSpacing: '0.01em',
                background: emailValid
                  ? `linear-gradient(135deg, ${brandPrimary} 0%, ${brandSecondary} 100%)`
                  : '#E5E7EB',
                color: emailValid ? '#FFFFFF' : '#9CA3AF',
                boxShadow: emailValid
                  ? '0 4px 14px rgba(79,70,229,0.25)'
                  : 'none',
                border: 'none',
                padding: 0,
                cursor: emailValid && !sendLoading ? 'pointer' : 'not-allowed',
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                {sendLoading ? (
                  <div
                    className="rounded-full border-2 animate-spin"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderColor: 'rgba(255,255,255,0.3)',
                      borderTopColor: '#ffffff',
                    }}
                  />
                ) : (
                  '发送验证码'
                )}
              </span>
            </button>

            {/* Error */}
            {error && (
              <p
                className="text-center mt-4"
                style={{ fontSize: '13px', color: '#EF4444' }}
              >
                {error}
              </p>
            )}
            {/* Agreement text */}
            <p
              className="text-center mt-5"
              style={{
                fontSize: '12px',
                color: '#9CA3AF',
                lineHeight: 1.6,
              }}
            >
              新用户首次登录需阅读并确认
              <span
                className="cursor-pointer"
                style={{ color: '#4F46E5', fontWeight: 500 }}
                onClick={e => { e.stopPropagation(); setShowAgreement(true); }}
              >
                《用户协议》《隐私政策》及《风险告知书》
              </span>
              {termsVersion ? `（${termsVersion}）` : ''}
            </p>
          </div>
        </div>
      )}

      {/* ─────────────── OTP / Verify State ─────────────── */}
      {otpSent && !needs2FA && (
        <div className="flex-1 flex flex-col px-6" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 72px)' }}>
          <button
            type="button"
            onClick={handleBackFromOtp}
            aria-label="返回"
            className="absolute flex items-center justify-center rounded-full"
            style={{
              top: 'max(env(safe-area-inset-top, 0px), 18px)',
              left: '18px',
              width: '40px',
              height: '40px',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              color: '#6B7280',
              boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          >
            <i className="ri-arrow-left-s-line" style={{ fontSize: '24px' }} />
          </button>

          {/* Compact Logo + Brand */}
          <div className="flex flex-col items-center mb-10">
            <div className="mb-3">
              <img
                src={forexMindLogo}
                alt="ForexMind"
                className="w-11 h-11 object-contain"
                style={{ filter: 'drop-shadow(0 6px 16px rgba(15, 23, 42, 0.12))' }}
              />
            </div>
            <h1
              className="font-bold tracking-tight"
              style={{
                fontSize: '22px',
                color: '#111827',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              ForexMind
            </h1>
          </div>

          <div className="w-full max-w-sm mx-auto">
            {/* Title */}
            <h2
              className="font-semibold"
              style={{
                fontSize: '20px',
                color: '#111827',
                letterSpacing: '-0.01em',
                marginBottom: '6px',
              }}
            >
              输入验证码
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: '#9CA3AF',
                marginBottom: '28px',
              }}
            >
              {debugOtpCode ? `预发环境验证码 ${debugOtpCode}` : `已发送至 ${email}`}
            </p>

            <OtpCodeInput
              value={otpCode}
              disabled={verifyLoading}
              autoFocusKey={otpSent}
              ariaLabel="输入邮箱验证码"
              onChange={next => {
                setOtpCode(next);
                setError('');
                if (next.length < OTP_LENGTH) lastAutoLoginOtpRef.current = '';
              }}
            />

            {/* Login Button */}
            <button
              type="button"
              onClick={() => doLogin()}
              disabled={!otpFull || verifyLoading}
              className="w-full font-semibold transition-all flex-shrink-0"
              style={{
                height: '56px',
                minHeight: '56px',
                borderRadius: '16px',
                fontSize: '16px',
                letterSpacing: '0.01em',
                background: otpFull
                  ? `linear-gradient(135deg, ${brandPrimary} 0%, ${brandSecondary} 100%)`
                  : '#E5E7EB',
                color: otpFull ? '#FFFFFF' : '#9CA3AF',
                boxShadow: otpFull
                  ? '0 4px 14px rgba(79,70,229,0.25)'
                  : 'none',
                border: 'none',
                padding: 0,
                cursor: otpFull && !verifyLoading ? 'pointer' : 'not-allowed',
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                {verifyLoading ? (
                  <div
                    className="rounded-full border-2 animate-spin"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderColor: 'rgba(255,255,255,0.3)',
                      borderTopColor: '#ffffff',
                    }}
                  />
                ) : (
                  '登录'
                )}
              </span>
            </button>

            {/* Error */}
            {error && (
              <p
                className="text-center mt-4"
                style={{ fontSize: '13px', color: '#EF4444' }}
              >
                {error}
              </p>
            )}

            {/* Bottom actions */}
            <div className="mt-6 flex items-center justify-center gap-1">
              <span style={{ fontSize: '14px', color: '#9CA3AF' }}>
                未收到验证码？
              </span>
              {countdown > 0 ? (
                <span style={{ fontSize: '14px', color: '#9CA3AF', fontWeight: 500 }}>
                  {countdown}s 后重新发送
                </span>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={sendLoading}
                  style={{
                    fontSize: '14px',
                    color: '#4F46E5',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    padding: '4px 0',
                    cursor: sendLoading ? 'not-allowed' : 'pointer',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    lineHeight: '1.5',
                  }}
                >
                  {sendLoading ? '发送中...' : '重新发送'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─────────────── 2FA Verification State ─────────────── */}
      {needs2FA && (
        <div className="flex-1 flex flex-col px-6" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 72px)' }}>
          <button
            type="button"
            onClick={handleBackFrom2FA}
            aria-label="返回"
            className="absolute flex items-center justify-center rounded-full"
            style={{
              top: 'max(env(safe-area-inset-top, 0px), 18px)',
              left: '18px',
              width: '40px',
              height: '40px',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              color: '#6B7280',
              boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          >
            <i className="ri-arrow-left-s-line" style={{ fontSize: '24px' }} />
          </button>

          {/* Compact Logo + Brand */}
          <div className="flex flex-col items-center mb-10">
            <div className="mb-3">
              <img
                src={forexMindLogo}
                alt="ForexMind"
                className="w-11 h-11 object-contain"
                style={{ filter: 'drop-shadow(0 6px 16px rgba(15, 23, 42, 0.12))' }}
              />
            </div>
            <h1
              className="font-bold tracking-tight"
              style={{
                fontSize: '22px',
                color: '#111827',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              ForexMind
            </h1>
          </div>

          <div className="w-full max-w-sm mx-auto">
            {/* Title */}
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-100">
                <i className="ri-shield-keyhole-line text-indigo-500 text-base" />
              </div>
              <h2
                className="font-semibold"
                style={{
                  fontSize: '20px',
                  color: '#111827',
                  letterSpacing: '-0.01em',
                }}
              >
                双重验证
              </h2>
            </div>
            <p
              style={{
                fontSize: '14px',
                color: '#9CA3AF',
                marginBottom: '28px',
                marginTop: '4px',
              }}
            >
              请输入 Authenticator App 中的 6 位验证码
            </p>

            <OtpCodeInput
              value={twoFACode}
              disabled={twoFALoading}
              autoFocusKey={needs2FA}
              ariaLabel="输入双重验证码"
              onChange={next => {
                setTwoFACode(next);
                setTwoFAError('');
              }}
            />

            {twoFAError && (
              <p
                className="text-center mb-3"
                style={{ fontSize: '13px', color: '#EF4444' }}
              >
                {twoFAError}
              </p>
            )}

            {/* Verify Button */}
            <button
              onClick={handle2FAVerify}
              disabled={!twoFAFull || twoFALoading}
              className="w-full font-semibold transition-all flex-shrink-0"
              style={{
                height: '56px',
                minHeight: '56px',
                borderRadius: '16px',
                fontSize: '16px',
                letterSpacing: '0.01em',
                background: twoFAFull
                  ? `linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)`
                  : '#E5E7EB',
                color: twoFAFull ? '#FFFFFF' : '#9CA3AF',
                boxShadow: twoFAFull
                  ? '0 4px 14px rgba(99,102,241,0.25)'
                  : 'none',
                border: 'none',
                padding: 0,
                cursor: twoFAFull && !twoFALoading ? 'pointer' : 'not-allowed',
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                {twoFALoading ? (
                  <div
                    className="rounded-full border-2 animate-spin"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderColor: 'rgba(255,255,255,0.3)',
                      borderTopColor: '#ffffff',
                    }}
                  />
                ) : (
                  '验证并登录'
                )}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ─────────────── Agreement Modal ─────────────── */}
      {showAgreement && (
        <AgreementModal
          docs={AGREEMENTS}
          onConfirm={handleAgreementConfirm}
        />
      )}
    </div>
  );
}
